import React, { useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import './Settings.css';

interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  assignedSignatures: string[];
}

interface Tenant {
  id: string;
  trial: boolean;
  consented: boolean;
  tenantCreated?: string;
  trialExpiration?: string;
  syncMode?: 'internal' | 'all';
}

interface SettingsProps {
  tenantId: string;
  onBack: () => void;
  initialTab?: 'account' | 'users' | 'deployment';
}

const Settings: React.FC<SettingsProps> = ({ tenantId, onBack, initialTab = 'account' }) => {
  const [activeTab, setActiveTab] = useState<'account' | 'users' | 'deployment'>(initialTab);
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<any>(null);
  const [syncModeUpdating, setSyncModeUpdating] = useState(false);
  const { instance, accounts } = useMsal();

  const getAccessToken = async () => {
    const account = accounts[0];
    if (!account) return null;
    try {
      const response = await instance.acquireTokenSilent({
        scopes: ['User.Read'],
        account: account
      });
      return response.accessToken;
    } catch (error) {
      console.error('Failed to acquire token:', error);
      return null;
    }
  };

  useEffect(() => {
    loadTenantInfo();
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  const loadTenantInfo = async () => {
    try {
      const token = await getAccessToken();
      if (!token) return;

      const response = await fetch(`/api/tenant/${tenantId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTenant(data.tenant);
      }
    } catch (error) {
      console.error('Failed to load tenant info:', error);
    }
  };

  const loadUsers = async () => {
    try {
      const token = await getAccessToken();
      if (!token) return;

      setLoading(true);
      const response = await fetch(`/api/users/${tenantId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
      }
    } catch (error) {
      console.error('Failed to load users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSyncUsers = async () => {
    try {
      const token = await getAccessToken();
      if (!token) return;

      setSyncing(true);
      const response = await fetch('/api/tenant/sync', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ tenantId })
      });

      if (response.ok) {
        const data = await response.json();
        setSyncResult(data);
        // Reload users to show newly synced ones
        await loadUsers();
      } else {
        setSyncResult({ error: 'Failed to sync users. Please try again.' });
      }
    } catch (error) {
      console.error('Failed to sync users:', error);
      setSyncResult({ error: 'An error occurred while syncing users.' });
    } finally {
      setSyncing(false);
    }
  };

  const handleSyncModeChange = async (newMode: 'internal' | 'all') => {
    try {
      const token = await getAccessToken();
      if (!token) return;

      setSyncModeUpdating(true);
      const response = await fetch('/api/tenant/update-sync-mode', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ tenantId, syncMode: newMode })
      });

      if (response.ok) {
        setTenant(prev => prev ? { ...prev, syncMode: newMode } : null);
      } else {
        alert('Failed to update sync mode');
      }
    } catch (error) {
      console.error('Failed to update sync mode:', error);
      alert('Failed to update sync mode');
    } finally {
      setSyncModeUpdating(false);
    }
  };

  const handleRoleToggle = async (userId: string, currentRole: string) => {
    const newRole = currentRole === 'TenantAdmin' ? 'User' : 'TenantAdmin';
    
    try {
      const token = await getAccessToken();
      if (!token) return;

      const response = await fetch('/api/user/update-role', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          userId,
          tenantId,
          role: newRole,
        }),
      });

      if (response.ok) {
        // Update local state
        setUsers(users.map(user => 
          user.id === userId ? { ...user, role: newRole } : user
        ));
      } else {
        alert('Failed to update user role');
      }
    } catch (error) {
      console.error('Failed to update role:', error);
      alert('Failed to update user role');
    }
  };

  const handleReconsent = () => {
    // Redirect to admin consent URL
    const consentUrl = `https://login.microsoftonline.com/${tenantId}/adminconsent?client_id=1e5948f3-987c-4b44-a803-8a77fd199a17&redirect_uri=${encodeURIComponent(window.location.origin)}`;
    window.open(consentUrl, '_blank');
  };

  const handleDownloadManifest = async () => {
    try {
      const manifestUrl = 'https://addin.stamprapp.com/manifest.xml';
      const response = await fetch(manifestUrl);
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'manifest.xml';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } else {
        alert('Failed to download manifest');
      }
    } catch (error) {
      console.error('Error downloading manifest:', error);
      alert('Failed to download manifest');
    }
  };

  const isTrialExpired = () => {
    if (!tenant?.trial || !tenant?.trialExpiration) return false;
    
    const expirationDate = new Date(tenant.trialExpiration);
    const now = new Date();
    
    return now > expirationDate;
  };;

  const getAccountTypeDisplay = () => {
    if (!tenant?.trial) {
      return { text: '✓ Subscription Active', className: 'subscription' };
    }
    if (isTrialExpired()) {
      return { text: '❌ Trial Expired', className: 'expired' };
    }
    return { text: 'Trial', className: 'trial' };
  };

  const accountType = getAccountTypeDisplay();

  return (
    <div className="settings-container">
      <div className="settings-header">
        <button className="btn-back" onClick={onBack}>
          ← Back
        </button>
        <h1>Settings</h1>
      </div>

      <div className="settings-tabs">
        <button
          className={`settings-tab ${activeTab === 'account' ? 'active' : ''}`}
          onClick={() => setActiveTab('account')}
        >
          Account Info
        </button>
        <button
          className={`settings-tab ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          User Administration
        </button>
        <button
          className={`settings-tab ${activeTab === 'deployment' ? 'active' : ''}`}
          onClick={() => setActiveTab('deployment')}
        >
          Deployment Instructions
        </button>
      </div>

      <div className="settings-content">
        {activeTab === 'account' && (
          <div className="account-info">
            <h2>Account Information</h2>
            <div className="info-card">
              <div className="info-row">
                <span className="info-label">Microsoft Tenant ID:</span>
                <span className="info-value">{tenantId}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Consent Status:</span>
                <div className="consent-status-group">
                  <span className={`info-value status ${tenant?.consented ? 'granted' : 'pending'}`}>
                    {tenant?.consented ? 'Consented' : '⚠ Needs Consent'}
                  </span>
                  {!tenant?.consented && (
                    <button className="btn-reconsent-inline" onClick={handleReconsent}>
                      🔄 Re-Consent Application
                    </button>
                  )}
                </div>
              </div>
              <div className="info-row">
                <span className="info-label">Account Type:</span>
                <span className={`info-value account-type ${accountType.className}`}>
                  {accountType.text}
                </span>
              </div>
              {isTrialExpired() && (
                <div className="info-row subscribe-prompt">
                  <div className="subscribe-message">
                    <p><strong>Your trial has expired.</strong> To continue using Stampr, please subscribe.</p>
                    <p>Contact <a href="mailto:sales@ryantechinc.com">sales@ryantechinc.com</a> to upgrade your account.</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'users' && (
          <div className="user-administration">
            <div className="user-admin-header">
              <h2>User Administration</h2>
              <button 
                className="btn-sync-users" 
                onClick={handleSyncUsers}
                disabled={syncing}
              >
                {syncing ? '🔄 Syncing...' : 'Sync Tenant'}
              </button>
            </div>
            
            <div className="sync-mode-toggle">
              <label className="sync-mode-label">Sync Mode:</label>
              <div className="sync-mode-options">
                <button
                  className={`sync-mode-option ${(tenant?.syncMode || 'internal') === 'internal' ? 'active' : ''}`}
                  onClick={() => handleSyncModeChange('internal')}
                  disabled={syncModeUpdating}
                >
                  Sync Internal Users Only
                </button>
                <button
                  className={`sync-mode-option ${(tenant?.syncMode || 'internal') === 'all' ? 'active' : ''}`}
                  onClick={() => handleSyncModeChange('all')}
                  disabled={syncModeUpdating}
                >
                  Sync Entire Directory
                </button>
              </div>
              <p className="sync-mode-description">
                {(tenant?.syncMode || 'internal') === 'internal' 
                  ? 'Only internal users will be synced (Guest and external users are excluded)'
                  : 'All directory members will be synced, including Guest and external users'}
              </p>
            </div>
            
            <div className="search-bar">
              <input
                type="text"
                placeholder="Search users by name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input"
              />
            </div>
            {loading ? (
              <div className="loading">Loading users...</div>
            ) : users.length === 0 ? (
              <p className="no-users">No users found.</p>
            ) : (
              <div className="users-table">
                <div className="table-header">
                  <div className="table-cell">Name</div>
                  <div className="table-cell">Email</div>
                  <div className="table-cell">Signature Assigned</div>
                  <div className="table-cell">Role</div>
                  <div className="table-cell">Actions</div>
                </div>
                {users
                  .filter(user => user.name.toLowerCase().includes(searchQuery.toLowerCase()))
                  .sort((a, b) => {
                    // TenantAdmin first, then User
                    if (a.role === 'TenantAdmin' && b.role !== 'TenantAdmin') return -1;
                    if (a.role !== 'TenantAdmin' && b.role === 'TenantAdmin') return 1;
                    // If same role, sort by name
                    return a.name.localeCompare(b.name);
                  })
                  .map(user => (
                  <div key={user.id} className="table-row">
                    <div className="table-cell">{user.name}</div>
                    <div className="table-cell">{user.email}</div>
                    <div className="table-cell">
                      <span className={`badge ${user.assignedSignatures?.length > 0 ? 'yes' : 'no'}`}>
                        {user.assignedSignatures?.length > 0 ? 'Yes' : 'No'}
                      </span>
                    </div>
                    <div className="table-cell">
                      <span className={`role-badge ${user.role === 'TenantAdmin' ? 'admin' : 'user'}`}>
                        {user.role}
                      </span>
                    </div>
                    <div className="table-cell">
                      <button
                        className="btn-toggle-role"
                        onClick={() => handleRoleToggle(user.id, user.role)}
                      >
                        Toggle Role
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'deployment' && (
          <div className="deployment-instructions">
            <h2>Deployment Instructions</h2>
            <div className="info-card">
              <button className="btn-download-manifest" onClick={handleDownloadManifest}>
                Download Latest Add-in Manifest
              </button>
              <div className="instructions-placeholder">
                <p>Step by step coming soon. Please email <a href="mailto:helpdesk@ryantechinc.com">helpdesk@ryantechinc.com</a> for support.</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Sync Modal */}
      {(syncing || syncResult) && (
        <div className="sync-modal-overlay" onClick={() => !syncing && setSyncResult(null)}>
          <div className="sync-modal" onClick={(e) => e.stopPropagation()}>
            {syncing ? (
              <div className="sync-in-progress">
                <div className="sync-spinner"></div>
                <h3>Syncing Tenant</h3>
                <p>Syncing users and signature assignments from Microsoft 365...</p>
              </div>
            ) : syncResult?.error ? (
              <div className="sync-error">
                <div className="error-icon">⚠️</div>
                <h3>Sync Failed</h3>
                <p>{syncResult.error}</p>
                <button className="btn-close-modal" onClick={() => setSyncResult(null)}>
                  Close
                </button>
              </div>
            ) : (
              <div className="sync-success">
                <div className="success-icon">✓</div>
                <h3>Tenant sync complete!</h3>
                <button className="btn-close-modal" onClick={() => setSyncResult(null)}>
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
